import { useState, useEffect } from 'react';
import { C } from '../../../lib/tokens.js';
import { isImage, isPdf } from '../../../lib/utils.js';
import { usePdfThumbnail } from '../../../lib/pdfThumbnail.js';
import { ImageLightbox } from '../../../components/ui/ImageLightbox.jsx';

// ─── Individual Thumbnail Item (Handles memory leak cleanups) ─────────────────
function ThumbnailItem({ file, onRemove, onPreview }) {
  const [src, setSrc] = useState(null);
  const typeIsImg = isImage(file.name) || file.type.startsWith('image/');
  const typeIsPdf = isPdf(file.name) || file.type === 'application/pdf';
  const pdfThumb = usePdfThumbnail(typeIsPdf ? file : null, { width: 128 });

  useEffect(() => {
    if (!typeIsImg) return;
    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);

    // Revoke object URL on unmount to prevent browser memory leaks
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, typeIsImg]);

  const handleRemove = (e) => {
    e.stopPropagation(); // Avoid triggering parent click previews
    if (onRemove) onRemove();
  };

  return (
    <div
      onClick={() => typeIsImg && src && onPreview(src)}
      style={{
        position: 'relative', borderRadius: 4, border: `1px solid ${C.mercury}`,
        overflow: 'hidden', background: C.platinum, flexShrink: 0,
        cursor: typeIsImg ? 'pointer' : 'default',
      }}
    >
      {typeIsImg && src ? (
        <img src={src} alt={file.name} style={{ width: 64, height: 64, objectFit: 'cover', display: 'block' }} />
      ) : typeIsPdf && pdfThumb ? (
        <img src={pdfThumb} alt={file.name} style={{ width: 64, height: 64, objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
      ) : (
        <div style={{ width: 64, height: 64, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: 4 }}>
          <span className="material-icons" style={{ fontSize: 22, color: typeIsPdf ? C.copper : C.iron }}>
            {typeIsPdf ? 'picture_as_pdf' : 'insert_drive_file'}
          </span>
          <span style={{ fontSize: 9, color: C.iron, textAlign: 'center', lineHeight: 1.2, wordBreak: 'break-all' }}>
            {file.name.length > 14 ? file.name.slice(0, 12) + '…' : file.name}
          </span>
        </div>
      )}
      {onRemove && (
        <button type="button" onClick={handleRemove}
          style={{
            position: 'absolute', top: 2, right: 2, width: 16, height: 16,
            borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)',
            color: '#fff', cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 0, zIndex: 2,
          }}>
          <span className="material-icons" style={{ fontSize: 11 }}>close</span>
        </button>
      )}
    </div>
  );
}

// ─── Shared attachment container with Lightbox Manager ───────────────────────
export function AttachmentThumbs({ files, onRemove }) {
  const [activePreviewUrl, setActivePreviewUrl] = useState(null);
  if (!files || !files.length) return null;

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {files.map((f, i) => (
          <ThumbnailItem
            key={`${f.name}-${i}`}
            file={f}
            onRemove={onRemove ? () => onRemove(i) : null}
            onPreview={setActivePreviewUrl}
          />
        ))}
      </div>

      {activePreviewUrl && (
        <ImageLightbox url={activePreviewUrl} onClose={() => setActivePreviewUrl(null)} />
      )}
    </>
  );
}
